import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import { AgencyNameSchema, type AgencyNameSchemaType } from "../../schema";
import SettingsItemContainer from "../SettingsItemContainer";

export default function Name() {
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);
  const { agencySettings, isLoadingAgencySettings } = useAgencySettings();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { name: agencySettings ? agencySettings.name : "" },
    resolver: valibotResolver(AgencyNameSchema),
  });

  useEffect(() => {
    if (agencySettings) {
      reset({ name: agencySettings.name });
    }
  }, [agencySettings]);

  function onSubmit({ name }: AgencyNameSchemaType) {
    updateAgencySettings({ updates: { name } }).then(() => {
      setIsEditing(false);
    });
  }
  function onCancel() {
    setIsEditing(false);
    reset({ name: agencySettings.name });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsItemContainer className="items-center">
        {isEditing ? (
          <FormField
            label="Agency name"
            htmlFor="agency-name"
            className="w-full"
            error={errors.name?.message}
            size={isDesktop ? "sm" : "lg"}
          >
            <div className="gap-y-4 gap-x-2 flex flex-col lg:flex-row">
              <Input
                id="agency-name"
                placeholder="Enter name"
                variant={isDesktop ? "sm" : "lg"}
                className="lg:w-80"
                autoFocus
                {...register("name")}
              />
              <div className="flex gap-x-2 mt-auto ml-auto">
                <Button
                  variant="secondary-black"
                  size={isDesktop ? "sm" : "md"}
                  onClick={onCancel}
                  disabled={isUpdatingAgencySettings}
                >
                  Cancel
                </Button>
                <Button
                  size={isDesktop ? "sm" : "md"}
                  type="submit"
                  variant="secondary-purple"
                  isLoading={isUpdatingAgencySettings}
                >
                  Save
                </Button>
              </div>
            </div>
          </FormField>
        ) : (
          <div className="flex flex-col gap-y-0.5">
            <span className="text-button-sm">Agency name</span>
            {isLoadingAgencySettings ? (
              <Skeleton />
            ) : (
              <span className="text-black-500 text-xs-medium">
                {agencySettings.name}
              </span>
            )}
          </div>
        )}
        {!isEditing && (
          <Button
            variant="ghost-black"
            intent="iconOnly"
            className="ml-auto"
            onClick={() => setIsEditing(true)}
          >
            <PencilSimpleLine />
          </Button>
        )}
      </SettingsItemContainer>
    </form>
  );
}
